/**
 * Vehicle-type icon library
 *
 * Every drawable the user can pick for a vehicle type lives here as an entry carrying its own
 * shape discriminator (`kind`), `viewBox`, tintability and the anchor/scale or pixel size that
 * kind needs — a bare path string is not an entry (D3). Three kinds:
 *
 * - `symbol`: a Google Maps `Symbol` (path + scale, anchored in path units), tinted by device
 *   state through `fillColor`.
 * - `glyph`: a boxicons font glyph harvested into `boxicons-glyphs.js`, wrapped on demand in a
 *   data-URI `<svg>` whose transform flips the font's y-up coordinates, tinted by interpolating
 *   `fill`.
 * - `markup`: an authored `<svg>` used verbatim as a data URI. Untinted entries keep every fill
 *   they were authored with; tinted entries carry `FILL_PLACEHOLDER` where the device-state
 *   colour goes.
 *
 * `buildIconConfig` is the only thing that turns an entry into a Maps icon config, and
 * `buildIconDataUri` the only thing that turns one into a data URI, so the map and the settings
 * preview cannot disagree about what an entry looks like.
 */

import {CUSTOM_ASSET_1, CUSTOM_ASSET_2, TRACKER, TRUCK} from "./svg-icons";
import {BOXICONS_GLYPHS} from "./boxicons-glyphs";

export const ICON_KIND = {
    SYMBOL: 'symbol',
    GLYPH: 'glyph',
    MARKUP: 'markup'
};

/**
 * The boxicons em-square in flipped coordinates: `units-per-em` 1024 with `ascent` 960, so the
 * font's own y range [-64, 960] maps to [64, 1088] under the flip below (see boxicons-glyphs.js).
 */
const GLYPH_VIEW_BOX = '0 64 1024 1024';
const GLYPH_FLIP_TRANSFORM = 'translate(0,1024) scale(1,-1)';

/**
 * Where a tinted markup entry takes the device-state colour.
 */
const FILL_PLACEHOLDER = '{fill}';

/**
 * Google Maps paints a symbol whose `fillColor` is undefined black; a data URI cannot leave the
 * fill undefined, so an untinted call resolves to the same black.
 */
const DEFAULT_ICON_FILL = '#000000';

/**
 * Hand-authored single-colour silhouettes for the vehicle classes no shipped font carries (N1).
 * Each is drawn upright in SVG's own y-down space, faces left like the boxicons vehicles, and
 * fills its viewBox so it stays readable at the 26px the settings preview draws it at.
 */
const SILHOUETTE_EXCAVATOR = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 48">
  <g fill="${FILL_PLACEHOLDER}">
    <rect x="20" y="32" width="42" height="14" rx="7"/>
    <rect x="34" y="14" width="22" height="18" rx="3"/>
    <path d="M38.4,23.4 L16.4,3.4 L11.6,8.6 L33.6,28.6 Z"/>
    <path d="M16.8,7 L8.8,29 L3.2,27 L11.2,5 Z"/>
    <path d="M12,26 L2,26 L5,40 L14,36 Z"/>
  </g>
</svg>`;

const SILHOUETTE_TANKER = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 48">
  <g fill="${FILL_PLACEHOLDER}">
    <path d="M2,14 a4,4 0 0 1 4,-4 h9 l6,8 h5 v14 h-24 z"/>
    <rect x="24" y="8" width="38" height="20" rx="10"/>
    <circle cx="12" cy="34" r="8"/>
    <circle cx="46" cy="34" r="8"/>
  </g>
</svg>`;

const SILHOUETTE_VAN = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 48">
  <g fill="${FILL_PLACEHOLDER}">
    <path d="M62,10 a4,4 0 0 0 -4,-4 h-36 l-10,10 h-4 a4,4 0 0 0 -4,4 v12 h58 z"/>
    <circle cx="18" cy="32" r="8"/>
    <circle cx="48" cy="32" r="8"/>
  </g>
</svg>`;

const SILHOUETTE_MOTORCYCLE = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 48">
  <g fill="${FILL_PLACEHOLDER}">
    <circle cx="13" cy="32" r="10"/>
    <circle cx="51" cy="32" r="10"/>
    <rect x="13" y="26" width="38" height="6" rx="3"/>
    <path d="M20,26 L24,16 L40,16 L46,26 Z"/>
    <path d="M20,8 L13,28 L18,30 L25,10 Z"/>
    <rect x="14" y="6" width="14" height="4" rx="2"/>
  </g>
</svg>`;

/**
 * Human labels for the harvested font glyphs, in the order the gallery shows them. Every name here
 * must exist in `BOXICONS_GLYPHS` and vice versa.
 */
const GLYPH_LABELS = {
    'bxs-truck': 'Truck',
    'bx-car': 'Car (outline)',
    'bxs-car': 'Car',
    'bx-bus': 'Bus (outline)',
    'bxs-bus': 'Bus',
    'bx-bus-school': 'School bus',
    'bx-taxi': 'Taxi (outline)',
    'bxs-taxi': 'Taxi',
    'bx-train': 'Train (outline)',
    'bxs-train': 'Train',
    'bxs-ship': 'Ship',
    'bxs-plane': 'Plane',
    'bxs-car-mechanic': 'Car mechanic',
    'bxs-car-garage': 'Garage',
    'bx-map-pin': 'Map pin (outline)',
    'bxs-map-pin': 'Map pin'
};

/**
 * Font glyphs and silhouettes are drawn square-ish in the box, so both render at 32px wide; the
 * silhouettes keep their 4:3 viewBox aspect.
 */
const GLYPH_SIZE = {sizeX: 32, sizeY: 32};
const SILHOUETTE_SIZE = {sizeX: 32, sizeY: 24};

const glyphEntries = Object.keys(GLYPH_LABELS).map(name => ({
    id: name,
    label: GLYPH_LABELS[name],
    kind: ICON_KIND.GLYPH,
    glyph: BOXICONS_GLYPHS[name],
    viewBox: GLYPH_VIEW_BOX,
    ...GLYPH_SIZE,
    anchorX: GLYPH_SIZE.sizeX / 2,
    anchorY: GLYPH_SIZE.sizeY / 2,
    tintable: true
}));

export const ICON_LIBRARY = [
    {
        id: 'default-truck',
        label: 'Truck (default)',
        kind: ICON_KIND.SYMBOL,
        path: TRUCK,
        viewBox: '0 -10 600 640',
        scale: 0.05,
        anchorX: 300,
        anchorY: 300,
        tintable: true
    },
    {
        id: 'default-tracker',
        label: 'Tracker (default)',
        kind: ICON_KIND.SYMBOL,
        path: TRACKER,
        viewBox: '0 0 440 450',
        scale: 0.1,
        anchorX: 200,
        anchorY: 350,
        tintable: true
    },
    {
        id: 'silhouette-excavator',
        label: 'Excavator',
        kind: ICON_KIND.MARKUP,
        markup: SILHOUETTE_EXCAVATOR,
        viewBox: '0 0 64 48',
        ...SILHOUETTE_SIZE,
        anchorX: SILHOUETTE_SIZE.sizeX / 2,
        anchorY: SILHOUETTE_SIZE.sizeY / 2,
        tintable: true
    },
    {
        id: 'silhouette-tanker',
        label: 'Tanker',
        kind: ICON_KIND.MARKUP,
        markup: SILHOUETTE_TANKER,
        viewBox: '0 0 64 48',
        ...SILHOUETTE_SIZE,
        anchorX: SILHOUETTE_SIZE.sizeX / 2,
        anchorY: SILHOUETTE_SIZE.sizeY / 2,
        tintable: true
    },
    {
        id: 'silhouette-van',
        label: 'Van',
        kind: ICON_KIND.MARKUP,
        markup: SILHOUETTE_VAN,
        viewBox: '0 0 64 48',
        ...SILHOUETTE_SIZE,
        anchorX: SILHOUETTE_SIZE.sizeX / 2,
        anchorY: SILHOUETTE_SIZE.sizeY / 2,
        tintable: true
    },
    {
        id: 'silhouette-motorcycle',
        label: 'Motorcycle',
        kind: ICON_KIND.MARKUP,
        markup: SILHOUETTE_MOTORCYCLE,
        viewBox: '0 0 64 48',
        ...SILHOUETTE_SIZE,
        anchorX: SILHOUETTE_SIZE.sizeX / 2,
        anchorY: SILHOUETTE_SIZE.sizeY / 2,
        tintable: true
    },
    ...glyphEntries,
    {
        id: 'asset-silver-disc',
        label: 'Silver disc asset',
        kind: ICON_KIND.MARKUP,
        markup: CUSTOM_ASSET_1,
        viewBox: '0 0 38 64',
        sizeX: 19,
        sizeY: 32,
        anchorX: 10,
        anchorY: 24,
        tintable: false
    },
    {
        id: 'asset-yellow-hauler',
        label: 'Yellow hauler asset',
        kind: ICON_KIND.MARKUP,
        markup: CUSTOM_ASSET_2,
        viewBox: '0 0 64 64',
        sizeX: 19,
        sizeY: 32,
        anchorX: 10,
        anchorY: 24,
        tintable: false
    }
];

/**
 * Lookup index over `ICON_LIBRARY`, for resolving a saved `iconID`.
 */
export const ICON_LIBRARY_BY_ID = ICON_LIBRARY.reduce((index, entry) => {
    index[entry.id] = entry;
    return index;
}, {});

/**
 * Data URIs are built per marker, and a fleet rebuild builds one per tracker, so identical
 * (entry, fill) pairs are memoized. Bounded by entries x device-state colours.
 */
const DATA_URI_CACHE = new Map();

const buildIconSvg = (entry, fillColor) => {
    const fill = fillColor ?? DEFAULT_ICON_FILL;
    if(entry.kind === ICON_KIND.GLYPH)
    {
        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${GLYPH_VIEW_BOX}"` +
            ` width="${entry.sizeX}" height="${entry.sizeY}">` +
            `<g transform="${GLYPH_FLIP_TRANSFORM}">` +
            `<path d="${entry.glyph}"${entry.tintable? ` fill="${fill}"`: ''}/>` +
            `</g></svg>`;
    }
    return entry.tintable? entry.markup.split(FILL_PLACEHOLDER).join(fill): entry.markup;
};

/**
 * The data URI a `glyph` or `markup` entry is drawn from, on the map and in the settings preview
 * alike.
 */
export const buildIconDataUri = (entry, fillColor) => {
    const cacheKey = `${entry.id}|${fillColor ?? ''}`;
    if(!DATA_URI_CACHE.has(cacheKey))
    {
        const svg = buildIconSvg(entry, fillColor);
        DATA_URI_CACHE.set(cacheKey, `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`);
    }
    return DATA_URI_CACHE.get(cacheKey);
};

/**
 * The Google Maps icon config for an entry: a `Symbol` for `symbol` entries, an `Icon` for
 * `glyph`/`markup` ones. The kind discriminates the shape, never the vehicle-type id.
 */
export const buildIconConfig = (entry, {maps, fillColor}) => {
    const shared = {
        anchorX: entry.anchorX,
        anchorY: entry.anchorY,
        fillOpacity: 1,
        strokeWeight: 0,
        anchor: new maps.Point(entry.anchorX, entry.anchorY)
    };
    if(entry.kind === ICON_KIND.SYMBOL)
    {
        return {
            path: entry.path,
            scale: entry.scale,
            ...shared,
            //an untinted symbol takes Maps' own default fill, as an absent fillColor always did
            ...(entry.tintable? {fillColor}: {})
        };
    }
    return {
        url: buildIconDataUri(entry, fillColor),
        sizeX: entry.sizeX,
        sizeY: entry.sizeY,
        ...shared,
        //the tint is baked into the data URI; Maps ignores fillColor on an Icon
        scaledSize: new maps.Size(entry.sizeX, entry.sizeY)
    };
};
